/**
 * Shared predicates for CRM activity status. Deriving these at call sites causes
 * subtle drift (e.g. an automated send that is already on its way rendering as an
 * overdue to-do) — keep the conditions here.
 */

/** Statuses in which nothing more will happen — the activity is closed, one way or another. */
export const TERMINAL_STATUSES = ['completed', 'failed', 'cancelled'];

/** Statuses hidden from the open activity lists. A failed send stays visible so it gets noticed. */
export const CLOSED_STATUSES = ['completed', 'cancelled'];

/**
 * Whether the activity is still awaiting action.
 * @param {object} activity
 * @returns {boolean}
 */
export const isOpenStatus = (activity) => !TERMINAL_STATUSES.includes(activity?.status);

/**
 * Whether an automated send is currently being handed off (email in flight, letter at the printer).
 * @param {object} activity
 * @returns {boolean}
 */
export const isSending = (activity) => activity?.status === 'in_progress';

/**
 * Whether an activity should be flagged as overdue.
 *
 * An automated activity is never overdue: once dueAt passes, a worker owns it. Between the due time
 * and the send confirmation (seconds for email, days for a letter) it is "wird versendet", not
 * "you forgot this". Likewise a terminal activity has nothing left to do.
 *
 * `dayStart` compares against midnight rather than the exact instant, so an activity due earlier
 * today is not already red. Callers that bucket by day pass their own boundary.
 *
 * @param {object} activity
 * @param {Date} [now]
 * @returns {boolean}
 */
export const isOverdue = (activity, now = new Date()) => {
	if (!activity?.dueAt) return false;
	if (activity.isAutomated) return false;
	if (!isOpenStatus(activity)) return false;
	const dayStart = new Date(now).setHours(0, 0, 0, 0);
	return new Date(activity.dueAt) < dayStart;
};
